import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layout, ChevronDown, ChevronRight, FileText, FolderOpen, Folder } from "lucide-react";

import webflowLogo from "@/assets/partners/webflow-icon.svg";
import wordpressLogo from "@/assets/partners/wordpress.svg";
import framerLogo from "@/assets/partners/framer.svg";
import lovableLogo from "@/assets/partners/lovable-icon.svg";

const techs = [
  { name: "Webflow", logo: webflowLogo },
  { name: "WordPress", logo: wordpressLogo },
  { name: "Framer", logo: framerLogo },
  { name: "Lovable", logo: lovableLogo },
];

const sitemap = [
  {
    name: "Home",
    active: true,
    children: [],
  },
  {
    name: "About",
    children: ["Our Story", "Team"],
  },
  {
    name: "Services",
    children: ["Web Development", "Ecommerce", "AI"],
  },
  {
    name: "Blog",
    children: [],
  },
  {
    name: "Contact",
    children: [],
  },
];

export function SitemapPreview() {
  const [techIdx, setTechIdx] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(["Services"]));
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleFolder = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {/* Sitemap tree */}
      <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]">
        <p className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
          <Layout className="h-3 w-3" />
          Sitemap
        </p>
        <div className="space-y-0.5">
          {sitemap.map((page, i) => {
            const hasChildren = page.children.length > 0;
            const isExpanded = expanded.has(page.name);

            return (
              <motion.div
                key={page.name}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: i * 0.04 }}
              >
                {/* Page row */}
                <button
                  onClick={() => hasChildren && toggleFolder(page.name)}
                  className={`w-full flex items-center gap-2 py-1.5 px-1 rounded-md text-left transition-colors ${
                    hasChildren ? "hover:bg-secondary/60 cursor-pointer" : "cursor-default"
                  }`}
                >
                  {/* Indicator / folder icon */}
                  {hasChildren ? (
                    <motion.div
                      animate={{ rotate: isExpanded ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                    </motion.div>
                  ) : (
                    <FileText className="h-3 w-3 text-muted-foreground" />
                  )}

                  {hasChildren ? (
                    isExpanded ? (
                      <FolderOpen className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <Folder className="h-3.5 w-3.5 text-muted-foreground" />
                    )
                  ) : null}

                  <span
                    className={`text-sm ${
                      page.active ? "text-primary font-medium" : "text-foreground"
                    }`}
                  >
                    {page.name}
                  </span>
                </button>

                {/* Sub-pages */}
                <AnimatePresence initial={false}>
                  {hasChildren && isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-5 pl-3 border-l border-border/60 space-y-0.5">
                        {page.children.map((child, j) => (
                          <motion.div
                            key={child}
                            className="flex items-center gap-2 py-1 px-1"
                            initial={{ opacity: 0, x: -4 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: j * 0.04 }}
                          >
                            <FileText className="h-3 w-3 text-muted-foreground/60" />
                            <span className="text-sm text-muted-foreground">{child}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Functional tech stack dropdown */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
        ref={dropRef}
        className="relative"
      >
        <p className="text-xs font-medium text-foreground mb-1">Tech Stack</p>
        <button
          onClick={() => setDropdownOpen((o) => !o)}
          className="w-full flex items-center justify-between bg-card border border-border rounded-lg px-3 py-2 shadow-[0_1px_2px_rgba(10,13,20,0.03)] hover:border-primary/20 transition-colors"
        >
          <div className="flex items-center gap-2">
            <img src={techs[techIdx].logo} alt="" className="h-4 w-4 object-contain" />
            <span className="text-sm text-foreground">{techs[techIdx].name}</span>
          </div>
          <motion.div animate={{ rotate: dropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </motion.div>
        </button>

        <AnimatePresence>
          {dropdownOpen && (
            <motion.div
              className="absolute bottom-full left-0 right-0 mb-1 bg-card border border-border rounded-lg shadow-card overflow-hidden z-20"
              initial={{ opacity: 0, y: 4, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.97 }}
              transition={{ duration: 0.15 }}
            >
              {techs.map((tech, i) => (
                <button
                  key={tech.name}
                  onClick={() => {
                    setTechIdx(i);
                    setDropdownOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                    i === techIdx
                      ? "bg-primary/5 text-primary font-medium"
                      : "text-foreground hover:bg-secondary/60"
                  }`}
                >
                  <img src={tech.logo} alt="" className="h-4 w-4 object-contain" />
                  {tech.name}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
